import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Briefcase,
  Scale,
  Home,
  Shield,
  Heart,
  MessageCircle,
  ChevronRight,
  CheckCircle2,
  Building2,
  Landmark,
  ArrowRight,
  Users,
  Target,
  Award,
  Gavel,
  Lock,
} from "lucide-react";

const LAWYER_HERO = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/ch185ger_IMG_7023.jpg";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const OFFICE_IMG = "https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxsYXclMjBvZmZpY2UlMjBwcm9mZXNzaW9uYWwlMjBsYXd5ZXIlMjBtZWV0aW5nfGVufDB8fHx8MTc3MjI0MjAyMnww&ixlib=rb-4.1.0&q=85";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de agendar uma consulta jurídica.";

const HomePage = () => {
  const areasDeAtuacao = [
    {
      icon: <Briefcase className="w-7 h-7" />,
      title: "Direito do Trabalho",
      desc: "Atuação estratégica em reclamações trabalhistas, defesa empresarial, recursos e consultoria preventiva.",
      path: "/direito-trabalhista",
    },
    {
      icon: <Home className="w-7 h-7" />,
      title: "Direito Imobiliário",
      desc: "Assessoria em compra e venda, contratos, regularização e disputas imobiliárias.",
      path: "/direito-imobiliario",
    },
    {
      icon: <Landmark className="w-7 h-7" />,
      title: "Direito Tributário",
      desc: "Planejamento tributário, recuperação de créditos e defesa em execuções fiscais.",
      path: "/direito-tributario",
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Direito Previdenciário",
      desc: "Concessão e revisão de aposentadorias e benefícios junto ao INSS.",
      path: "/direito-previdenciario",
    },
    {
      icon: <Scale className="w-7 h-7" />,
      title: "Direito Cível",
      desc: "Responsabilidade civil, indenizações, cobranças e contratos.",
      path: "/direito-civel",
    },
    {
      icon: <Heart className="w-7 h-7" />,
      title: "Direito de Família",
      desc: "Divórcio, guarda, alimentos e partilha de bens.",
      path: "/direito-familia",
    },
    {
      icon: <Lock className="w-7 h-7" />,
      title: "Direito Penal",
      desc: "Defesa criminal, habeas corpus, audiências de custódia e recursos.",
      path: "/direito-penal",
    },
  ];

  const diferenciais = [
    { icon: <Users className="w-6 h-6" />, title: "Atendimento Direto", desc: "Contato direto com o advogado responsável pelo seu caso" },
    { icon: <Target className="w-6 h-6" />, title: "Estratégia Personalizada", desc: "Cada caso recebe análise e planejamento específico" },
    { icon: <Building2 className="w-6 h-6" />, title: "Visão Empresarial", desc: "Abordagem preventiva e gestão de riscos jurídicos" },
    { icon: <Award className="w-6 h-6" />, title: "Atuação Técnica", desc: "Compromisso com ética, responsabilidade e excelência" },
  ];

  return (
    <>
      <Helmet>
        <title>Helio Lopes Leal | Advocacia e Consultoria Jurídica</title>
        <meta name="description" content="Advocacia estratégica para pessoas e empresas. Atuação em Direito Trabalhista, Imobiliário, Tributário, Previdenciário, Cível, Família e Penal." />
        <meta name="keywords" content="advogado, advogado trabalhista, advogado imobiliário, direito tributário, advogado criminalista, advocacia empresarial, consultoria jurídica" />
      </Helmet>

      <div>
        {/* Hero Section */}
        <section className="min-h-[90vh] flex items-center bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
          {/* Background Pattern - Logo with elegant transparency */}
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute right-0 top-1/4 w-[800px] h-[800px]">
              <img src={LOGO_ICON} alt="" className="w-full h-full object-contain" style={{ filter: "brightness(0)" }} />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Content */}
              <div>
                <p className="text-blue-900 font-medium tracking-wider text-sm mb-4">
                  HELIO LOPES LEAL ADVOCACIA
                </p>
                <h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Advocacia Estratégica para{" "}
                  <span className="text-blue-900">Pessoas e Empresas</span>
                </h1>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl">
                  Atuação técnica e personalizada em Direito Trabalhista, Imobiliário, 
                  Tributário, Previdenciário, Cível, Família e Penal.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-8 py-4 rounded font-medium transition-all"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Fale com um Advogado
                  </a>
                  <Link
                    to="/sobre"
                    className="inline-flex items-center justify-center gap-2 border-2 border-gray-300 hover:border-blue-900 text-gray-700 hover:text-blue-900 px-8 py-4 rounded font-medium transition-all"
                  >
                    Conheça o Escritório
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              {/* Image */}
              <div className="hidden lg:flex justify-end">
                <div className="relative">
                  <div className="absolute -inset-4 bg-blue-900/10 rounded-2xl transform rotate-3"></div>
                  <img
                    src={LAWYER_HERO}
                    alt="Dr. Helio Lopes Leal - Advogado"
                    className="relative rounded-2xl shadow-2xl w-full max-w-md object-cover"
                    style={{ aspectRatio: "3/4" }}
                  />
                  <div className="absolute -bottom-6 -left-6 bg-white px-6 py-4 rounded-lg shadow-xl">
                    <p className="text-xs text-gray-500 mb-1">Registro Profissional</p>
                    <p className="font-bold text-gray-900">OAB/SP 396.732</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Autoridade Section */}
        <section className="py-20 bg-white border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 
                className="text-3xl font-bold text-gray-900 mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Experiência e Compromisso com Resultados
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Graduado em Direito pela FMU, com experiência prática em audiências e sustentação oral 
                perante tribunais. Atuação consolidada na defesa de trabalhadores e empresas, 
                com foco em gestão de contingências e estratégia processual personalizada.
              </p>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 text-blue-900 font-medium hover:gap-3 transition-all"
              >
                Conheça nossa história
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Áreas de Atuação */}
        <section className="py-24 bg-gray-50 relative overflow-hidden">
          {/* Background Logo */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[600px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <p className="text-blue-900 font-medium tracking-wider text-sm mb-3">ESPECIALIDADES</p>
              <h2 
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Áreas de Atuação
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Assessoria jurídica completa e estratégica para proteger seus direitos e interesses
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {areasDeAtuacao.map((area, index) => (
                <Link
                  key={index}
                  to={area.path}
                  className="group bg-white p-8 rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-blue-50 rounded-lg flex items-center justify-center text-blue-900 mb-6 group-hover:bg-blue-900 group-hover:text-white transition-all">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-900 transition-colors">
                    {area.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{area.desc}</p>
                  <span className="inline-flex items-center text-blue-900 text-sm font-medium group-hover:gap-2 transition-all">
                    Saiba mais <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Diferenciais */}
        <section className="py-24 bg-white relative overflow-hidden">
          {/* Background Logo */}
          <div className="absolute right-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-blue-900 font-medium tracking-wider text-sm mb-3">POR QUE NOS ESCOLHER</p>
                <h2 
                  className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Diferenciais do Nosso Escritório
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Combinamos conhecimento técnico com uma abordagem estratégica e personalizada, 
                  sempre focados em oferecer as melhores soluções jurídicas para cada situação.
                </p>
                <div className="space-y-6">
                  {diferenciais.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-900 flex-shrink-0">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 p-10 rounded-2xl">
                <h3 
                  className="text-2xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Nosso Compromisso
                </h3>
                <ul className="space-y-4">
                  {[
                    "Atendimento direto com advogado",
                    "Estratégia processual personalizada",
                    "Visão empresarial e preventiva",
                    "Comunicação clara e objetiva",
                    "Atuação técnica e responsável",
                    "Transparência em honorários",
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-900 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Jurisprudência Preview */}
        <section className="py-20 bg-blue-900 relative overflow-hidden">
          {/* Background Logo */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.05] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto invert" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <Gavel className="w-12 h-12 text-blue-300 mx-auto mb-6" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-white mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Consulta de Jurisprudência
              </h2>
              <p className="text-blue-200 mb-8">
                Acesse os portais oficiais dos principais tribunais brasileiros para 
                consultar decisões e jurisprudência atualizada.
              </p>
              <Link
                to="/julgados"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-blue-900 px-8 py-4 rounded font-medium transition-all"
              >
                Acessar Tribunais
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-24 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <img src={LOGO_ICON} alt="" className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-auto invert" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 
              className="text-3xl md:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Segurança Jurídica Começa com a Orientação Certa
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Entre em contato e agende uma consulta com nossa equipe. 
              Estamos prontos para ajudar você.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Solicitar Atendimento
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center gap-2 border-2 border-gray-600 hover:border-white text-gray-300 hover:text-white px-8 py-4 rounded font-medium transition-all"
              >
                Outras Formas de Contato
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
